import { CircleMinus, CirclePlus, Pencil, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CopyButton } from '@/components/ui/CopyButton'
import { Pagination } from '@/components/ui/pagination'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { SearchInput } from '@/components/ui/search-input'
import {
  SortableTableHead,
  SortDirection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableCard, TableCardField, TableCardList } from '@/components/ui/table-card'
import { useTableSearch } from '@/hooks/useTableSearch'
import { Demo, DsSection } from '../primitives'

interface TruckRow {
  vin: string
  year: string
  make: string
  model: string
  value: string
}

const TRUCKS: TruckRow[] = [
  { vin: '1FUJGLDR9LLAA1234', year: '2020', make: 'Freightliner', model: 'Cascadia', value: '$92,000' },
  { vin: '1XKYDP9X7MJ412345', year: '2021', make: 'Kenworth', model: 'T680', value: '$118,500' },
  { vin: '4V4NC9EH8NN567890', year: '2022', make: 'Volvo', model: 'VNL 760', value: '$134,000' },
]

interface DriverRow {
  id: string
  name: string
  mobile?: string
  email?: string
  /** Required on an application — a blank one warns rather than showing a dash. */
  dob?: string
  dateOfHire?: string
  cdlDate?: string
}

const DRIVERS: DriverRow[] = [
  {
    id: 'd1',
    name: 'Marcus Webb',
    mobile: '(214) 555-0182',
    email: 'marcus.webb@example.com',
    dob: '03/14/1985',
    dateOfHire: '06/01/2021',
    cdlDate: '08/22/2008',
  },
  {
    id: 'd2',
    name: 'Dana Ortiz',
    mobile: '(915) 555-0447',
    email: 'dana.ortiz@example.com',
    dob: '11/02/1979',
    dateOfHire: '02/17/2023',
  },
  {
    id: 'd3',
    name: 'Ray Whitfield',
    email: 'r.whitfield@example.com',
    // '' rather than absent: the edit form saves untouched fields this way.
    dob: '',
    dateOfHire: '09/30/2024',
    cdlDate: '01/09/2015',
  },
  {
    id: 'd4',
    name: 'Tanya Brooks',
    mobile: '(602) 555-0913',
    email: 'tanya.brooks@example.com',
    dob: '07/25/1990',
    dateOfHire: '04/11/2022',
    cdlDate: '05/30/2013',
  },
  {
    id: 'd5',
    name: 'Luis Delgado',
    mobile: '(505) 555-0264',
    dob: '01/18/1974',
    dateOfHire: '11/08/2019',
    cdlDate: '03/14/2001',
  },
  {
    id: 'd6',
    name: 'Priya Raman',
    mobile: '(773) 555-0656',
    email: 'p.raman@example.com',
    dob: '09/06/1988',
    dateOfHire: '01/22/2025',
  },
]

/** Module-level so the hook's filter memo keeps a stable reference. */
function driverSearchValues(driver: DriverRow) {
  return [driver.name, driver.mobile, driver.email]
}

/** Blank required field — the table shows this in place of a value, so cards do too. */
function MissingValue() {
  return <TriangleAlert className="text-warning-d1 h-5 w-5" />
}

/** Blank optional field. `TableCardField` renders the same dash on its own. */
function BlankValue() {
  return <span className="text-muted-foreground">—</span>
}

function DriverActions() {
  return (
    <>
      <Button
        type="button"
        variant="iconOnly"
        className="hover:text-brand-800 text-neutral-500"
        aria-label="Edit driver"
      >
        <Pencil />
      </Button>
      <Button
        type="button"
        variant="iconOnly"
        className="hover:text-error text-neutral-500"
        aria-label="Remove driver"
      >
        <CircleMinus />
      </Button>
    </>
  )
}

function DriverCards({ drivers }: { drivers: DriverRow[] }) {
  return (
    <TableCardList>
      {drivers.map((driver) => (
        <TableCard key={driver.id} title={driver.name} actions={<DriverActions />} onOpen={() => {}}>
          <TableCardField label="Mobile" value={driver.mobile} />
          <TableCardField label="Email" value={driver.email} />
          <TableCardField label="Date of birth" value={driver.dob || <MissingValue />} />
          <TableCardField label="Hire date" value={driver.dateOfHire || <MissingValue />} />
          <TableCardField label="CDL original issue date" value={driver.cdlDate} />
        </TableCard>
      ))}
    </TableCardList>
  )
}

/** The same useTableSearch hook the tables use, driving cards instead of rows. */
function DriverCardStack() {
  const { query, onQueryChange, page, setPage, totalPages, pageItems, filtered, rangeSummary } =
    useTableSearch({ items: DRIVERS, getSearchValues: driverSearchValues, pageSize: 3 })

  return (
    <div className="flex flex-col gap-3">
      <SearchInput
        value={query}
        onValueChange={onQueryChange}
        placeholder="Search drivers..."
        aria-label="Search drivers"
      />
      {filtered.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">No drivers match “{query}”</p>
      ) : (
        <DriverCards drivers={pageItems} />
      )}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        summary={rangeSummary('drivers')}
      />
    </div>
  )
}

/** One list of columns feeds both the table and the cards — this is what production passes. */
const DRIVER_COLUMNS: ResponsiveTableColumn<DriverRow>[] = [
  {
    key: 'name',
    header: 'Name',
    headClassName: 'w-[100px]',
    cellClassName: 'font-medium',
    // Leads the card as its title instead.
    hideOnCard: true,
    cell: (driver) => driver.name,
  },
  { key: 'mobile', header: 'Mobile', cell: (driver) => driver.mobile || <BlankValue /> },
  { key: 'email', header: 'Email', cell: (driver) => driver.email || <BlankValue /> },
  { key: 'dob', header: 'Date of birth', cell: (driver) => driver.dob || <MissingValue /> },
  { key: 'dateOfHire', header: 'Hire date', cell: (driver) => driver.dateOfHire || <MissingValue /> },
  { key: 'cdlDate', header: 'CDL original issue date', cell: (driver) => driver.cdlDate || <BlankValue /> },
]

enum TruckSortColumn {
  MAKE = 'make',
  YEAR = 'year',
}

/** Make and Year sort; Model stays a plain header. */
function SortableTruckTable() {
  const [sortColumn, setSortColumn] = useState(TruckSortColumn.YEAR)
  const [direction, setDirection] = useState(SortDirection.DESCENDING)
  const ascending = direction === SortDirection.ASCENDING
  const sorted = [...TRUCKS].sort((a, b) => a[sortColumn].localeCompare(b[sortColumn]) * (ascending ? 1 : -1))

  function sortBy(column: TruckSortColumn) {
    if (column === sortColumn) {
      setDirection(ascending ? SortDirection.DESCENDING : SortDirection.ASCENDING)
      return
    }
    setSortColumn(column)
    setDirection(SortDirection.ASCENDING)
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead
            className="font-semibold"
            direction={sortColumn === TruckSortColumn.MAKE ? direction : undefined}
            onSort={() => sortBy(TruckSortColumn.MAKE)}
          >
            Make
          </SortableTableHead>
          <TableHead className="font-semibold">Model</TableHead>
          <SortableTableHead
            className="font-semibold"
            direction={sortColumn === TruckSortColumn.YEAR ? direction : undefined}
            onSort={() => sortBy(TruckSortColumn.YEAR)}
          >
            Year
          </SortableTableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((truck) => (
          <TableRow key={truck.vin}>
            <TableCell>{truck.make}</TableCell>
            <TableCell>{truck.model}</TableCell>
            <TableCell>{truck.year}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const PARTS = [
  '<Table>',
  '<TableHeader>',
  '<TableBody>',
  '<TableRow>',
  '<TableHead>',
  '<TableCell>',
  '<TableFooter>',
  '<TableCaption>',
]

/** Mono name plus its copy button — the variant label every section on this page uses. */
function Ref({ value }: { value: string }) {
  return (
    <div className="text-ink-400 flex items-center gap-1 font-mono text-[10px]">
      <span>{value}</span>
      <CopyButton
        value={value}
        className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
      />
    </div>
  )
}

function RefRow({ values }: { values: string[] }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {values.map((value) => (
        <Ref key={value} value={value} />
      ))}
    </div>
  )
}

export function TableSection() {
  return (
    <DsSection id="table" title="Table">
      <Demo label="Parts" className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">VIN</TableHead>
              <TableHead className="font-semibold">Year</TableHead>
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="font-semibold">Model</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRUCKS.map((truck) => (
              <TableRow key={truck.vin}>
                <TableCell className="font-medium">{truck.vin}</TableCell>
                <TableCell>{truck.year}</TableCell>
                <TableCell>{truck.make}</TableCell>
                <TableCell>{truck.model}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <RefRow values={PARTS} />
      </Demo>

      <Demo label="TableHead — weight, alignment, width" className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12" />
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="text-center font-semibold">Year</TableHead>
              <TableHead className="text-right font-semibold">Stated value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRUCKS.map((truck, index) => (
              <TableRow key={truck.vin}>
                <TableCell className="text-ink-400">{index + 1}</TableCell>
                <TableCell>{truck.make}</TableCell>
                <TableCell className="text-center">{truck.year}</TableCell>
                <TableCell className="text-right">{truck.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <RefRow
          values={[
            'className="font-semibold"',
            'className="text-center"',
            'className="text-right"',
            'className="w-12"',
          ]}
        />
      </Demo>

      <Demo label="SortableTableHead — unsorted, ascending, descending" className="flex flex-col gap-4">
        <SortableTruckTable />
        <RefRow
          values={[
            '<SortableTableHead>',
            'direction={SortDirection.ASCENDING}',
            'direction={SortDirection.DESCENDING}',
            'onSort',
          ]}
        />
      </Demo>

      <Demo label="TableRow — default, selected" className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="font-semibold">Model</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRUCKS.map((truck, index) => (
              <TableRow key={truck.vin} data-state={index === 1 ? 'selected' : undefined}>
                <TableCell>
                  <Checkbox checked={index === 1} aria-label={`Select ${truck.make}`} />
                </TableCell>
                <TableCell>{truck.make}</TableCell>
                <TableCell>{truck.model}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <RefRow values={['data-state="selected"', '[role=checkbox]']} />
      </Demo>

      <Demo label="Empty state — a single full-width row" className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">VIN</TableHead>
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="font-semibold">Model</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground pt-5 text-center">
                No trucks yet
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <RefRow values={['colSpan={3}', 'className="text-muted-foreground pt-5 text-center"']} />
      </Demo>

      <Demo
        label="Overflow — the container scrolls horizontally and fades both edges"
        className="flex flex-col gap-4"
      >
        {/* Constrained to roughly a phone's width so the shadows engage on desktop too. */}
        <div className="max-w-[360px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">VIN</TableHead>
                <TableHead className="font-semibold">Year</TableHead>
                <TableHead className="font-semibold">Make</TableHead>
                <TableHead className="font-semibold">Model</TableHead>
                <TableHead className="text-right font-semibold">Stated value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {TRUCKS.map((truck) => (
                <TableRow key={truck.vin}>
                  <TableCell className="font-medium">{truck.vin}</TableCell>
                  <TableCell>{truck.year}</TableCell>
                  <TableCell>{truck.make}</TableCell>
                  <TableCell>{truck.model}</TableCell>
                  <TableCell className="text-right">{truck.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <RefRow values={['data-slot="table-container"', 'overflow-x-auto', 'whitespace-nowrap']} />
      </Demo>

      <Demo
        label="Narrow screens — search above, one card per record, pagination below"
        className="flex flex-col gap-4"
      >
        {/* Held at a phone's width so the card treatment reads the same on desktop. */}
        <div className="max-w-[360px]">
          <DriverCardStack />
        </div>
        <RefRow
          values={[
            '<TableCardList>',
            '<TableCard>',
            '<TableCardField>',
            'title',
            'actions',
            'useTableSearch({ pageSize: 3 })',
          ]}
        />
      </Demo>

      <Demo
        label="ResponsiveTable — cards below lg, table at lg and up (resize to swap)"
        className="flex flex-col gap-4"
      >
        <ResponsiveTable
          items={DRIVERS}
          columns={DRIVER_COLUMNS}
          getRowId={(driver) => driver.id}
          cardTitle={(driver) => driver.name}
          onRowClick={() => {}}
          rowActions={() => <DriverActions />}
          empty="No drivers match your search"
          caption={
            <div className="flex w-full flex-wrap items-center gap-2">
              <Button type="button" variant="outline">
                <CirclePlus />
                Add driver
              </Button>
            </div>
          }
        />
        <RefRow
          values={['<ResponsiveTable>', 'columns', 'cardTitle', 'rowActions', 'caption', 'hideOnCard']}
        />
      </Demo>

      <Demo label="TableFooter — totals row" className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="font-semibold">Model</TableHead>
              <TableHead className="text-right font-semibold">Stated value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRUCKS.map((truck) => (
              <TableRow key={truck.vin}>
                <TableCell>{truck.make}</TableCell>
                <TableCell>{truck.model}</TableCell>
                <TableCell className="text-right">{truck.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2}>3 trucks</TableCell>
              <TableCell className="text-right">$344,500</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
        <RefRow values={['<TableFooter>', 'colSpan={2}']} />
      </Demo>

      <Demo label="TableCaption — the action slot under the rows" className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="font-semibold">Model</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRUCKS.map((truck) => (
              <TableRow key={truck.vin}>
                <TableCell>{truck.make}</TableCell>
                <TableCell>{truck.model}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableCaption>
            <div className="flex w-full items-center gap-2">
              <Button variant="outline" size="sm">
                Add truck
              </Button>
            </div>
          </TableCaption>
        </Table>
        <RefRow values={['<TableCaption>', 'caption-bottom']} />
      </Demo>
    </DsSection>
  )
}
